import React from 'react';
import { Card } from '../ui/Card';
import { TrendingUp, TrendingDown, Users, Eye, MousePointer, Clock, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Legend } from 'recharts';
import { Badge } from '../ui/Badge';

interface AnalyticsOverviewProps {
  dateRange: {
    from: Date;
    to: Date;
  };
  projectId?: string;
}

export function AnalyticsOverview({ dateRange, projectId }: AnalyticsOverviewProps) {
  const analytics = useAnalytics(projectId);
  const { data: analyticsData, isLoading, error } = analytics.useOverview(dateRange);

  // Use analytics data if available, otherwise fallback to mock data
  const stats = analyticsData?.overview || {
    sessions: 0,
    users: 0,
    pageviews: 0,
    bounceRate: 0,
    avgSessionDuration: 0,
    conversions: 0
  };

  // Calculate bounce rate as percentage with 1 decimal place
  const bounceRatePercent = stats.bounceRate ? (stats.bounceRate * 100).toFixed(1) : '0.0';

  // Format session duration
  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const metrics = [
    {
      title: 'Total Users',
      value: stats.users?.toLocaleString() || '0',
      change: '+12.5%', // TODO: Calculate actual change from previous period
      trend: 'up',
      icon: Users,
      color: 'blue'
    },
    {
      title: 'Page Views',
      value: stats.pageviews?.toLocaleString() || '0',
      change: '+8.2%', // TODO: Calculate actual change from previous period
      trend: 'up',
      icon: Eye,
      color: 'green'
    },
    {
      title: 'Bounce Rate',
      value: `${bounceRatePercent}%`,
      change: '-3.1%', // TODO: Calculate actual change from previous period
      trend: 'down',
      icon: MousePointer,
      color: 'orange'
    },
    {
      title: 'Avg. Session Duration',
      value: formatDuration(stats.avgSessionDuration || 0),
      change: '+15.7%', // TODO: Calculate actual change from previous period
      trend: 'up',
      icon: Clock,
      color: 'purple'
    }
  ];

  // Process top pages from analytics data
  const topPages = analyticsData?.overview?.topPages?.slice(0, 5)?.map((page: { page: string; pageviews: number }, index: number) => {
    const totalPageviews = analyticsData.overview.topPages.reduce((sum: number, p: { pageviews: number }) => sum + p.pageviews, 0);
    return {
      page: page.page,
      views: page.pageviews,
      percentage: totalPageviews > 0 ? Math.round((page.pageviews / totalPageviews) * 100) : 0
    };
  }) || [
    { page: '/home', views: 0, percentage: 0 },
    { page: '/products', views: 0, percentage: 0 },
    { page: '/about', views: 0, percentage: 0 },
    { page: '/contact', views: 0, percentage: 0 },
    { page: '/blog', views: 0, percentage: 0 }
  ];

  // Process traffic sources - use actual GA4 names and colors
  function getColorForSource(source: string, index: number = 0) {
    const ga4Colors: Record<string, string> = {
      'Organic Search': 'bg-blue-500',
      'Direct': 'bg-green-500',
      'Paid Search': 'bg-orange-500',
      'Display': 'bg-purple-500',
      'Social': 'bg-pink-500',
      'Email': 'bg-cyan-500',
      'Referral': 'bg-lime-500',
      'Affiliates': 'bg-red-500',
      'Video': 'bg-indigo-500',
      'Audio': 'bg-violet-500',
      'SMS': 'bg-teal-500',
      'Mobile Push Notifications': 'bg-blue-600',
      'Unassigned': 'bg-gray-500',
      'Organic Social': 'bg-pink-400',
      'Paid Social': 'bg-pink-600',
      'Organic Video': 'bg-red-400',
      'Paid Video': 'bg-red-600',
    };

    if (ga4Colors[source]) return ga4Colors[source];
    
    const fallbackColors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 
      'bg-pink-500', 'bg-cyan-500', 'bg-lime-500', 'bg-red-500'
    ];
    return fallbackColors[index % fallbackColors.length];
  }

  const trafficSources = analyticsData?.overview?.trafficSources?.slice(0, 5)?.map((source: { source: string; sessions: number; percentage: number }, index: number) => ({
    source: source.source,
    users: source.sessions, // Use sessions as user count
    percentage: source.percentage,
    color: getColorForSource(source.source, index),
    icon: TrendingUp,
    description: 'Traffic channel'
  })) || [
    { source: 'Organic Search', users: 0, percentage: 0, color: 'bg-blue-500', icon: TrendingUp, description: 'Search engines' },
    { source: 'Direct', users: 0, percentage: 0, color: 'bg-green-500', icon: TrendingUp, description: 'Direct traffic' },
    { source: 'Social Media', users: 0, percentage: 0, color: 'bg-purple-500', icon: TrendingUp, description: 'Social platforms' },
    { source: 'Referral', users: 0, percentage: 0, color: 'bg-orange-500', icon: TrendingUp, description: 'Other websites' },
    { source: 'Email', users: 0, percentage: 0, color: 'bg-pink-500', icon: TrendingUp, description: 'Email campaigns' }
  ];

  // Helper function to parse GA4 date format (YYYYMMDD) to readable format
  const parseGA4Date = (dateString: string) => {
    if (!dateString) return 'Invalid Date';
    
    // Handle different date formats
    if (dateString.includes('-')) {
      // Already in YYYY-MM-DD format
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else if (dateString.length === 8) {
      // GA4 format: YYYYMMDD
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      const date = new Date(`${year}-${month}-${day}`);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // Try to parse as regular date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Process daily trend data for the chart
  const chartData = analyticsData?.overview?.dailyTrend?.map((day: any) => ({
    date: parseGA4Date(day.date),
    users: day.users || 0,
    sessions: day.sessions || 0,
    pageviews: day.pageviews || 0
  })) || Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      users: Math.floor(Math.random() * 200) + 50,
      sessions: Math.floor(Math.random() * 300) + 100,
      pageviews: Math.floor(Math.random() * 500) + 200
    };
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="text-center">
            <p className="text-red-600">Error loading analytics data</p>
            <p className="text-sm text-red-500 mt-1">{error.message}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Data Source Indicator */}
      {analyticsData?.dataSource === 'ga4_real' && (
        <Card className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <Activity className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Real GA4 Data</h3>
              <p className="text-sm text-gray-600">
                Showing live data from Google Analytics 4
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{metric.value}</p>
                <div className="flex items-center mt-2">
                  {metric.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4 text-green-600 dark:text-green-400 mr-1" />
                  ) : metric.trend === 'down' ? (
                    <ArrowDownRight className="w-4 h-4 text-red-600 dark:text-red-400 mr-1" />
                  ) : null}
                  <span className={`text-sm font-medium ${
                    metric.trend === 'up' ? 'text-green-600 dark:text-green-400' : 
                    metric.trend === 'down' ? 'text-red-600 dark:text-red-400' : 
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {metric.change}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last period</span>
                </div>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                metric.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                metric.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                metric.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
                'bg-orange-100 dark:bg-orange-900/30'
              }`}>
                <metric.icon className={`w-6 h-6 ${
                  metric.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                  metric.color === 'green' ? 'text-green-600 dark:text-green-400' :
                  metric.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                  'text-orange-600 dark:text-orange-400'
                }`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Pages</h3>
          <div className="space-y-4">
            {topPages.map((page: { page: string; views: number; percentage: number }, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{page.page}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${page.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <p className="font-semibold text-gray-900 dark:text-white">{page.views.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">{page.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Traffic Sources */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Traffic Sources</h3>
          <div className="space-y-3">
            {trafficSources.map((source: any, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                    source.color?.includes('blue') ? 'bg-blue-100 dark:bg-blue-900/30' :
                    source.color?.includes('green') ? 'bg-green-100 dark:bg-green-900/30' :
                    source.color?.includes('purple') ? 'bg-purple-100 dark:bg-purple-900/30' :
                    'bg-orange-100 dark:bg-orange-900/30'
                  }`}>
                    <TrendingUp className={`w-4 h-4 ${
                      source.color?.includes('blue') ? 'text-blue-600 dark:text-blue-400' :
                      source.color?.includes('green') ? 'text-green-600 dark:text-green-400' :
                      source.color?.includes('purple') ? 'text-purple-600 dark:text-purple-400' :
                      'text-orange-600 dark:text-orange-400'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{source.source}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{source.description || 'Traffic source'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900 dark:text-white">{source.users.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{source.percentage}% of traffic</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Users Over Time Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Users Over Time</h3>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-gray-600 dark:text-gray-300">Users</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-600 dark:text-gray-300">Sessions</span>
            </div>
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01}/>
                </linearGradient>
                <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.01}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12 }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value: number, name: string) => [
                  value.toLocaleString(),
                  name === 'users' ? 'Users' : name === 'sessions' ? 'Sessions' : 'Pageviews'
                ]}
                labelStyle={{ color: '#1f2937', fontWeight: 600 }}
              />
              <Area
                type="monotone"
                dataKey="users"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#colorUsers)"
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
              />
              <Area
                type="monotone"
                dataKey="sessions"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#colorSessions)"
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 flex justify-between">
          <span>
            {analyticsData?.dataSource === 'ga4_real' ? 'Live GA4 data' : 'Sample data'} • 
            {chartData.length} days
          </span>
          <span>
            Total: {chartData.reduce((sum: number, day: any) => sum + day.users, 0).toLocaleString()} users
          </span>
        </div>
      </Card>

      {/* Performance Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Trends</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Key metrics over time</p>
          </div>
          {analyticsData?.dataSource && (
            <Badge variant={analyticsData.dataSource === 'ga4_real' ? 'success' : 'secondary'}>
              {analyticsData.dataSource === 'ga4_real' ? 'Real Data' : 'Sample Data'}
            </Badge>
          )}
        </div>
        
        {analyticsData?.charts?.performanceTrends?.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analyticsData.charts.performanceTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-color)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--text-muted)"
                  fontSize={12}
                />
                <YAxis stroke="var(--text-muted)" fontSize={12} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="sessions" 
                  stroke="var(--color-blue)" 
                  strokeWidth={2}
                  name="Sessions"
                />
                <Line 
                  type="monotone" 
                  dataKey="pageviews" 
                  stroke="var(--color-green)" 
                  strokeWidth={2}
                  name="Page Views"
                />
                <Line 
                  type="monotone" 
                  dataKey="users" 
                  stroke="var(--color-purple)" 
                  strokeWidth={2}
                  name="Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">
                {isLoading ? 'Loading performance data...' : 'No performance data available'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Data will appear here once your analytics start collecting information
              </p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
} 